import { Action } from '@/_core/fluxstore/types';
import { mapDtoPropsToCamelCase } from '@/_core/utils/mapDtos';
import type { NextGenRestoredCards } from '../../../api/commands';
import { ActivityModel, SubtaskModel, TaskModel } from '../../../api/queries';
import { Activity, StorymapState, Subtask, Task } from '../../types';
import { refreshTaskReleases } from '../../use/useTaskRelease';
import { useStorymapStore } from '../../useModule';

const { registerReducer } = useStorymapStore();

export class MergeRestoredCardsFromTrash extends Action<StorymapState> {
    public static ACTION_TYPE = 'MergeRestoredCardsFromTrash';

    public constructor(public restoredCards: NextGenRestoredCards = emptyRestoredCards()) {
        super(MergeRestoredCardsFromTrash.ACTION_TYPE);
    }
}

export const mergeRestoredCardsFromTrash = (state: StorymapState, restoredCards: NextGenRestoredCards = emptyRestoredCards()): void => {
    const payloadActivityIds = new Set((restoredCards?.Activities ?? []).map((x) => x.Id));
    const payloadTaskIds = new Set((restoredCards?.Tasks ?? []).map((x) => x.Id));
    const payloadSubtaskIds = new Set((restoredCards?.Subtasks ?? []).map((x) => x.Id));
    const activities = (restoredCards?.Activities ?? []).map(mapActivity).sort(compareActivitiesForRestore);
    const tasks = (restoredCards?.Tasks ?? []).map(mapTask).sort(compareTasksForRestore);
    const subtasks = (restoredCards?.Subtasks ?? []).map(mapSubtask).sort(compareSubtasksForRestore);

    for (const activity of activities) {
        insertOrUpdateActivity(state, activity, payloadActivityIds);
    }

    for (const task of tasks) {
        insertOrUpdateTask(state, task, payloadTaskIds);
    }

    for (const subtask of subtasks) {
        insertOrUpdateSubtask(state, subtask, payloadSubtaskIds);
    }

    trackRestoredCardsForHideUnfiltered(state, payloadActivityIds, payloadTaskIds, payloadSubtaskIds);
    refreshTaskReleases();
};

const reducer = (state: StorymapState, action: MergeRestoredCardsFromTrash): void => {
    mergeRestoredCardsFromTrash(state, action.restoredCards);
};

const emptyRestoredCards = (): NextGenRestoredCards => ({
    Activities: [],
    Tasks: [],
    Subtasks: [],
});

const frontendDefaults = () => ({
    isActive: false,
    activeUsers: [] as string[],
    isMultiselected: false,
    isTitleEditing: false,
    isNew: false,
    isSaving: false,
    isCopied: false,
    isCut: false,
});

const mapActivity = (serverSideModel: ActivityModel): Activity => ({
    ...mapDtoPropsToCamelCase(serverSideModel),
    ...frontendDefaults(),
    personaIds: serverSideModel.PersonaIds ?? [],
    annotationIds: serverSideModel.AnnotationIds ?? [],
    isCollapsed: false,
    createdAt: new Date(serverSideModel.CreatedAt),
    lastUpdatedAt: new Date(serverSideModel.LastUpdatedAt),
} as Activity);

const mapTask = (serverSideModel: TaskModel): Task => ({
    ...mapDtoPropsToCamelCase(serverSideModel),
    ...frontendDefaults(),
    personaIds: serverSideModel.PersonaIds ?? [],
    annotationIds: serverSideModel.AnnotationIds ?? [],
    createdAt: new Date(serverSideModel.CreatedAt),
    lastUpdatedAt: new Date(serverSideModel.LastUpdatedAt),
} as Task);

const mapSubtask = (serverSideModel: SubtaskModel): Subtask => ({
    ...mapDtoPropsToCamelCase(serverSideModel),
    ...frontendDefaults(),
    personaIds: serverSideModel.PersonaIds ?? [],
    annotationIds: serverSideModel.AnnotationIds ?? [],
    createdAt: new Date(serverSideModel.CreatedAt),
    lastUpdatedAt: new Date(serverSideModel.LastUpdatedAt),
} as Subtask);

const compareActivitiesForRestore = (a: Activity, b: Activity): number =>
    compareNumbers(a.order, b.order) || compareStrings(a.id, b.id);

const compareTasksForRestore = (a: Task, b: Task): number =>
    compareStrings(a.activityId, b.activityId) || compareNumbers(a.order, b.order) || compareStrings(a.id, b.id);

const compareSubtasksForRestore = (a: Subtask, b: Subtask): number =>
    compareStrings(a.taskId, b.taskId) ||
    compareStrings(a.releaseId, b.releaseId) ||
    compareNumbers(a.order, b.order) ||
    compareStrings(a.id, b.id);

const compareNumbers = (a: number, b: number): number => a - b;

const compareStrings = (a: string | null, b: string | null): number => (a ?? '').localeCompare(b ?? '');

const insertOrUpdateActivity = (state: StorymapState, activity: Activity, payloadActivityIds: Set<string>): void => {
    insertOrUpdateById(state.activities, activity, () => {
        state.activities
            .filter((x) => !payloadActivityIds.has(x.id) && x.order >= activity.order)
            .forEach((x) => x.order++);
    });
};

const insertOrUpdateTask = (state: StorymapState, task: Task, payloadTaskIds: Set<string>): void => {
    insertOrUpdateById(state.tasks, task, () => {
        state.tasks
            .filter((x) => !payloadTaskIds.has(x.id) && x.activityId === task.activityId && x.order >= task.order)
            .forEach((x) => x.order++);
    });
};

const insertOrUpdateSubtask = (state: StorymapState, subtask: Subtask, payloadSubtaskIds: Set<string>): void => {
    insertOrUpdateById(state.subtasks, subtask, () => {
        state.subtasks
            .filter((x) =>
                !payloadSubtaskIds.has(x.id) &&
                x.taskId === subtask.taskId &&
                x.releaseId === subtask.releaseId &&
                x.order >= subtask.order
            )
            .forEach((x) => x.order++);
    });
};

const insertOrUpdateById = <T extends Activity | Task | Subtask>(items: T[], item: T, beforeInsert: () => void): void => {
    const index = items.findIndex((x) => x.id === item.id);
    if (index >= 0) {
        items.splice(index, 1, { ...item, ...getLocalCardState(items[index]) });
    } else {
        beforeInsert();
        items.push(item);
    }
};

const trackRestoredCardsForHideUnfiltered = (
    state: StorymapState,
    activityIds: Set<string>,
    taskIds: Set<string>,
    subtaskIds: Set<string>
): void => {
    if (!state.filterState.hideUnfiltered) {
        return;
    }

    const restoredIds = [...activityIds, ...taskIds, ...subtaskIds];
    const trackedIds = new Set(state.filterState.newlyCreatedCardIds);
    for (const id of restoredIds) {
        trackedIds.add(id);
    }
    state.filterState.newlyCreatedCardIds = [...trackedIds];
};

const getLocalCardState = <T extends Activity | Task | Subtask>(item: T): Partial<T> => {
    const localState: Partial<T> = {
        isActive: item.isActive,
        activeUsers: item.activeUsers,
        isMultiselected: item.isMultiselected,
        isTitleEditing: item.isTitleEditing,
        isNew: item.isNew,
        isSaving: item.isSaving,
        isCopied: item.isCopied,
        isCut: item.isCut,
        isWatched: item.isWatched,
    } as Partial<T>;

    if ('isCollapsed' in item) {
        (localState as Partial<Activity>).isCollapsed = item.isCollapsed;
    }

    return localState;
};

//@ts-ignore
registerReducer(MergeRestoredCardsFromTrash.ACTION_TYPE, reducer);
